import type {
  ExtendedProductVariant,
  WovarProduct,
} from "@commercekit/api";
import type { HTMLComponent } from "@commercekit/react";
import { useCart, useLocale } from "@commercekit/remix";
import {
  fallbackMoney,
  formatMoneyWithCurrency,
  type Money,
} from "@commercekit/shared";
import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PrismicImage } from "@prismicio/react";
import type { ImageField } from "@prismicio/client";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import WvLink from "../atoms/WvLink";
import WvRelatedProductItem from "./WvRelatedProductItem";
import { useUnitOfMeasure } from "~/hooks/useUnitOfMeasure";

const hasMoney = (money?: Money | null) => Boolean(money?.currencyCode);

const WvAddToCartPopup: HTMLComponent<{
  relatedProducts?: WovarProduct[];
  updateSelectedProductVariant: ExtendedProductVariant | undefined;
  selectedUomAndQuantity: string;
  onClose(): void;
}> = ({
  onClose,
  relatedProducts,
  updateSelectedProductVariant,
  selectedUomAndQuantity,
}) => {
  const { localeFullName } = useLocale();
  const selectedUomProperty = selectedUomAndQuantity?.split("_")[0] ?? "";
  const quantity = Number(selectedUomAndQuantity?.split("_")[1] ?? 1);

  const { resultQueue, clearResult } = useCart();

  // Static hammer icon from Prismic
  const hammerIcon = {
    url: "https://images.prismic.io/wovar-rb2-dev/aWUByQIvOtkhBUnI_hammer-1-.png?auto=format,compress",
    alt: "Hammer icon",
    dimensions: { width: 512, height: 512 },
  } as ImageField;

  const [relatedProductsInCart, setRelatedProductsInCart] = useState<
    WovarProduct[]
  >([]);

  const selectedUom = useMemo(() => {
    if (!selectedUomProperty || !updateSelectedProductVariant?.unitsOfMeasure) {
      return undefined;
    }
    return updateSelectedProductVariant.unitsOfMeasure.find(
      (item) => item.uomProperty === selectedUomProperty
    );
  }, [selectedUomProperty, updateSelectedProductVariant?.unitsOfMeasure]);

  const newProductSet = useMemo(
    () =>
      resultQueue.filter((result) => {
        return (
          !relatedProductsInCart.find((item) => item.id === result.id) &&
          !result.actionFail
        );
      }),
    [resultQueue, relatedProductsInCart]
  );

  useEffect(() => {
    if (resultQueue?.length && newProductSet?.length) {
      setRelatedProductsInCart((items) => [...items, ...newProductSet]);
      clearResult();
    }
  }, [resultQueue, newProductSet, clearResult]);

  const salesPriceInclTax = useMemo(() => {
    return (
      selectedUom?.salePriceInclTax ??
      updateSelectedProductVariant?.salePriceInclTax
    );
  }, [selectedUom, updateSelectedProductVariant]);

  const salesPriceExclTax = useMemo(() => {
    return (
      selectedUom?.salePriceExclTax ??
      updateSelectedProductVariant?.salePriceExclTax
    );
  }, [selectedUom, updateSelectedProductVariant]);

  const priceInclTax = useMemo(() => {
    return (
      selectedUom?.priceInclTax ?? updateSelectedProductVariant?.priceInclTax
    );
  }, [selectedUom, updateSelectedProductVariant]);

  const priceExclTax = useMemo(() => {
    return (
      selectedUom?.priceExclTax ?? updateSelectedProductVariant?.priceExclTax
    );
  }, [selectedUom, updateSelectedProductVariant]);

  const isPriceReady = useMemo(() => {
    if (!updateSelectedProductVariant || !selectedUomProperty) {
      return false;
    }

    const uomCatalog = updateSelectedProductVariant.unitsOfMeasure;
    if (uomCatalog?.length && !selectedUom) {
      return false;
    }

    const onSale =
      hasMoney(salesPriceInclTax) && (salesPriceInclTax?.value ?? 0) > 0;
    if (onSale) {
      return (
        hasMoney(salesPriceInclTax) &&
        hasMoney(salesPriceExclTax) &&
        hasMoney(priceInclTax)
      );
    }
    return hasMoney(priceInclTax) && hasMoney(priceExclTax);
  }, [
    updateSelectedProductVariant,
    selectedUomProperty,
    selectedUom,
    salesPriceInclTax,
    salesPriceExclTax,
    priceInclTax,
    priceExclTax,
  ]);

  const isProductInCart = useCallback(
    (product: WovarProduct) =>
      relatedProductsInCart.find((item) => item.id === product.id)
        ? true
        : false,
    [relatedProductsInCart]
  );

  const RelatedProductInCartRow: FC<{
    product: WovarProduct;
  }> = ({ product }) => {
    const { $pt } = usePrismicLabels();
    return (
      <div
        className="popup-add-to-cart__relatedproduct row no-gutters align-items-start  w-100 popup-add-to-cart__relatedproduct--selected"
        data-id={product?.id}
      >
        <div className="col col-lg-9">
          <span
            data-icon-before=""
            className="col mr-3 px-0 popup-add-to-cart__relatedproduct-heading"
          >
            {product?.name}
          </span>
        </div>
        {product?.salePriceInclTax ? (
          <div className="col-auto col-lg-3 pr-3 pr-lg-0">
            <span className="d-lg-block price--old mr-2">
              {formatMoneyWithCurrency(
                localeFullName,
                product?.priceInclTax?.currencyCode
                  ? product?.priceInclTax
                  : fallbackMoney()
              )}
            </span>
            <span className="d-lg-inline-block price mr-2">
              {formatMoneyWithCurrency(
                localeFullName,
                product?.salePriceInclTax?.currencyCode
                  ? product?.salePriceInclTax
                  : fallbackMoney()
              )}
            </span>
            <span className="d-lg-inline-block price--vat mt-1">
              {formatMoneyWithCurrency(
                localeFullName,
                product?.salePriceExclTax?.currencyCode
                  ? product?.salePriceExclTax
                  : fallbackMoney()
              )}{" "}
              {$pt("product_label_excluding_tax")}
            </span>
          </div>
        ) : (
          <div className="col-auto col-lg-3 pr-3 pr-lg-0">
            <span className="d-lg-inline-block price mr-2">
              {formatMoneyWithCurrency(
                localeFullName,
                product?.priceInclTax?.currencyCode
                  ? product?.priceInclTax
                  : fallbackMoney()
              )}
            </span>
            <span className="d-lg-inline-block price--vat mt-1">
              {formatMoneyWithCurrency(
                localeFullName,
                product?.priceExclTax?.currencyCode
                  ? product?.priceExclTax
                  : fallbackMoney()
              )}{" "}
              {$pt("product_label_excluding_tax")}
            </span>
          </div>
        )}
      </div>
    );
  };
  const { $pt } = usePrismicLabels();
  const getUnitOfMeasure = useUnitOfMeasure();
  return (
    <>
      <div className="popup popup--open" data-popup="productdetail.add-to-cart">
        <div
          onClick={onClose}
          className="popup__overlay"
          style={{ visibility: "hidden" }}
        ></div>
        <div className="popup__inner px-3 py-1 py-lg-4 px-lg-5">
          <div className="popup__header pb-3 pb-lg-4">
            <div className="row no-gutters">
              <div className="col align-self-center">
                <h3
                  className="popup__heading heading sans-serif strong md mb-0 fill-success"
                  data-icon-before=""
                >
                  {$pt("product_added_to_cart_message")}
                </h3>
              </div>

              <div className="col-auto" onClick={onClose}>
                <div className="popup__close" data-icon-after=""></div>
              </div>
            </div>
          </div>

          <div className="popup__content">
            <div className="popup-add-to-cart">
              <div className="row">
                <div className="col-12 col-lg mb-3">
                  <div className="popup-add-to-cart__product row no-gutters align-items-start">
                    <div className="d-none d-lg-block popup-add-to-cart__product-image img col-auto mr-3">
                      <img
                        src={updateSelectedProductVariant?.defaultImage?.url}
                        alt={
                          updateSelectedProductVariant?.defaultImage?.altText
                        }
                        loading="lazy"
                      />
                    </div>
                    <div className="col row no-gutters">
                      <div className=" md pr-lg-3 col-lg-9">
                        <h3 className="heading sans-serif strong">
                          {updateSelectedProductVariant?.name}
                          {selectedUomAndQuantity && ": "}
                        </h3>
                        {selectedUomAndQuantity && (
                          <p>
                            <span className="pr-1">
                              {getUnitOfMeasure(selectedUomProperty)}
                            </span>
                            {(Number(selectedUom?.quantity) ?? 0) > 1 && (
                              <span>
                                {`(${Number(selectedUom?.quantity).toFixed(
                                  0
                                )} ${$pt("uom_pieces")})`}{" "}
                              </span>
                            )}
                            <span>
                              x {selectedUomAndQuantity.split("_")[1]}
                            </span>
                          </p>
                        )}
                      </div>

                      <div className="popup-add-to-cart__product-price col-lg-3 mb-3">
                        {isPriceReady &&
                          (salesPriceInclTax &&
                          (salesPriceInclTax?.value ?? 0) > 0 ? (
                          <>
                            <span
                              className="d-lg-bloc d-block price--old"
                              data-price={(priceInclTax?.value ?? 0) * quantity}
                            >
                              {formatMoneyWithCurrency(
                                localeFullName,
                                priceInclTax?.currencyCode
                                  ? priceInclTax
                                  : fallbackMoney(),
                                quantity
                              )}
                            </span>
                            <span
                              className="d-lg-inline-bloc price mr-1"
                              data-price={salesPriceInclTax?.value * quantity}
                            >
                              {formatMoneyWithCurrency(
                                localeFullName,
                                salesPriceInclTax?.currencyCode
                                  ? salesPriceInclTax
                                  : fallbackMoney(),
                                quantity
                              )}
                            </span>
                            <span className="d-lg-inline-bloc price--vat mt-1">
                              <span
                                data-price={
                                  (salesPriceExclTax?.value ?? 0) * quantity
                                }
                              >
                                {formatMoneyWithCurrency(
                                  localeFullName,
                                  salesPriceExclTax?.currencyCode
                                    ? salesPriceExclTax
                                    : fallbackMoney(),
                                  quantity
                                )}
                              </span>
                              {$pt("product_label_excluding_tax")}
                            </span>
                          </>
                        ) : (
                          <>
                            <span
                              className="d-lg-inline-bloc price mr-1"
                              data-price={(priceInclTax?.value ?? 0) * quantity}
                            >
                              {formatMoneyWithCurrency(
                                localeFullName,
                                priceInclTax ?? fallbackMoney(),
                                quantity
                              )}
                            </span>
                            <span className="d-lg-inline-bloc price--vat mt-1">
                              <span
                                data-price={
                                  (priceExclTax?.value ?? 0) * quantity
                                }
                              >
                                {formatMoneyWithCurrency(
                                  localeFullName,
                                  priceExclTax ?? fallbackMoney(),
                                  quantity
                                )}
                              </span>
                              {$pt("product_label_excluding_tax")}
                            </span>
                          </>
                        ))}
                      </div>
                      {relatedProductsInCart?.length
                        ? relatedProductsInCart.map((product, index) => (
                            <RelatedProductInCartRow
                              product={product}
                              key={`${product?.id}_${index}`}
                            />
                          ))
                        : null}
                    </div>
                  </div>
                </div>
                <div className="col-auto">
                  <button className="d-flex align-items-center btn btn--primary-green w-full text-white normal-case">
                    <WvLink to="/cart/">
                      <span className="mr-3">
                        <svg
                          width="25"
                          height="23"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g fill="#fff" fillRule="nonzero">
                            <path d="M8.53 18.882a1.882 1.882 0 100 3.765 1.882 1.882 0 000-3.765zM21.706 18.882a1.882 1.882 0 100 3.765 1.882 1.882 0 000-3.765zM4.765.059c.45 0 .836.318.923.757l.004.02L8.378 16.06h14.27c.513 0 .93.411.94.922V17a.941.941 0 01-.922.941H7.588a.941.941 0 01-.923-.757l-.004-.02L3.975 1.94H1a.941.941 0 01-.941-.922V1C.059.486.47.069.98.059H4.765z"></path>
                            <path d="M23.673 3.864c.636-.205 1.338.338 1.21 1.063l-.005.022-1.694 8.47a.941.941 0 01-.902.757H7.588a.941.941 0 01-.018-1.882h13.92l1.317-6.588H5.706a.941.941 0 01-.941-.923v-.018c0-.514.411-.931.922-.941h17.705c.09 0 .178.012.263.037l.014.004h.004z"></path>
                          </g>
                        </svg>
                      </span>{" "}
                      {$pt("shopping_cart_action_go_to_shopping_cart_page")}
                    </WvLink>
                  </button>

                  <button
                    type="button"
                    className="btn btn--gray-alt d-lg-block text-center link gray serif mt-2 ml-lg-auto text-black normal-case"
                    data-icon-after=""
                    onClick={onClose}
                  >
                    {$pt("shopping_cart_action_continue_shopping")}
                  </button>
                </div>
              </div>

              <div className="popup-add-to-cart__upsells mt-3 pt-3 pt-lg-4">
                {relatedProducts?.length ? (
                  <div className="row mb-2">
                    <div className="col-12">
                      <div className="d-flex align-items-center mb-3">
                        {hammerIcon && (
                          <div className="mr-3">
                            <PrismicImage
                              field={hammerIcon}
                              width={32}
                              height={32}
                              loading="lazy"
                            />
                          </div>
                        )}
                        <div>
                          <h3 className="heading sans-serif strong md mb-1">
                            {$pt("product_label_upsell_heading")}
                          </h3>
                          <p className="mb-0">
                            {$pt("product_label_upsell_description")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="row-scroller mx-n3 mx-md-0 pl-3 pl-md-0 mb-3">
                  <div className="row mx-n3 mx-md-n2 pl-2 pl-md-0 no-gutters justify-content-start pb-3">
                    {relatedProducts?.length
                      ? relatedProducts?.map((product, index) => (
                          <WvRelatedProductItem
                            product={product}
                            isAdded={isProductInCart(product)}
                            key={`${product.id}_${index}`}
                            itemListId="related_prodict_add_to_cart_pop_up"
                            itemListName="Related Product Add To Cart Popup"
                          />
                        ))
                      : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WvAddToCartPopup;
